import { Entity } from '@core/entities/entity'
import { plainToInstance, Type } from 'class-transformer'
import { v4 as uuidv4 } from 'uuid'
import { TemplateForm } from '@core/entities/template/TemplateForm/TemplateForm'
import {
  ITemplateQuestion,
  TemplateQuestion,
} from '@core/entities/template/TemplateQuestion'
import { ITemplateQuestionGroup } from '@core/entities/template/TemplateQuestionGroup/ITemplateQuestionGroup'
import { IAnswersWrapper } from '@core/entities/template/AnswersWrapper'
import { ICompetencyTemplateVersion } from '@core/entities/competency/CompetencyTemplateVersion/ICompetencyTemplateVersion'
import {
  CompetencyInductionFormValues,
  CompetencyTemplateVersionStatus,
} from '@core/entities/competency/CompetencyTemplateVersion/types'
import { ICompetencyTemplateUser } from '@core/entities/competency/CompetencyTemplateUser'
import { statuses, StatusColor } from '@core/theme/statuses'

const DAYS_PER_YEAR = 365
const DAYS_PER_MONTH = 30
const MINUTES_PER_HOUR = 60

export class CompetencyTemplateVersion extends Entity<ICompetencyTemplateVersion> {
  id: number

  templateId: number

  version: number

  status: CompetencyTemplateVersionStatus

  contentUrl?: string

  contentDescription?: string

  estimatedDurationMinutes?: number

  expiryDurationEnabled: boolean

  expiryDurationDays?: number

  @Type(() => TemplateForm)
  questionsWrapper?: TemplateForm

  createdOn?: string

  modifiedOn?: string

  createdBy?: ICompetencyTemplateUser

  modifiedBy?: ICompetencyTemplateUser

  public static new(payload: unknown): CompetencyTemplateVersion {
    return plainToInstance(CompetencyTemplateVersion, payload)
  }

  public isDraft(): boolean {
    return this.status === 'DRAFT'
  }

  public isActive(): boolean {
    return this.status === 'ACTIVE'
  }

  public isArchived(): boolean {
    return this.status === 'ARCHIVED'
  }

  public isDiscarded(): boolean {
    return this.status === 'DISCARDED'
  }

  public hasQuizQuestions(): boolean {
    return this.getFlatQuestions().some(
      (q) => q?.questionType === 'QUIZ_QUESTION',
    )
  }

  public isReadOnly(): boolean {
    return !this.isDraft()
  }

  public canActivate(): boolean {
    if (!this.isDraft()) return false
    if (!this.hasQuizQuestions()) return false
    if (!this.contentUrl?.trim()) return false
    if (this.expiryDurationEnabled && (this.expiryDurationDays ?? 0) < 1) {
      return false
    }
    return true
  }

  public getPreviousVersionNumber(): number | null {
    return this.version > 1 ? this.version - 1 : null
  }

  public getFlatQuestions(): Array<ITemplateQuestion> {
    const groups = this.questionsWrapper?.getInitialValues?.()?.questions ?? []
    return groups.flatMap(
      (group) => group?.data ?? [],
    ) as Array<ITemplateQuestion>
  }

  public getExpiryDurationSplit(): {
    years: number
    months: number
    days: number
  } {
    return CompetencyTemplateVersion.daysToYearsMonthsDays(
      this.expiryDurationDays,
    )
  }

  public getEstimatedDurationSplit(): { hours: number; minutes: number } {
    return CompetencyTemplateVersion.minutesToHoursMinutes(
      this.estimatedDurationMinutes,
    )
  }

  public static daysToYearsMonthsDays(totalDays?: number): {
    years: number
    months: number
    days: number
  } {
    if (!totalDays || totalDays <= 0) return { years: 1, months: 0, days: 0 }
    const years = Math.floor(totalDays / DAYS_PER_YEAR)
    const remainderAfterYears = totalDays - years * DAYS_PER_YEAR
    const months = Math.floor(remainderAfterYears / DAYS_PER_MONTH)
    const days = remainderAfterYears - months * DAYS_PER_MONTH
    return { years, months, days }
  }

  public static yearsMonthsDaysToDays(
    years: number,
    months: number,
    days: number,
  ): number {
    return Math.max(
      0,
      Math.floor(years || 0) * DAYS_PER_YEAR +
        Math.floor(months || 0) * DAYS_PER_MONTH +
        Math.floor(days || 0),
    )
  }

  public static minutesToHoursMinutes(totalMinutes?: number): {
    hours: number
    minutes: number
  } {
    const total = totalMinutes ?? 0
    return {
      hours: Math.floor(total / MINUTES_PER_HOUR),
      minutes: total % MINUTES_PER_HOUR,
    }
  }

  public static hoursMinutesToMinutes(hours: number, minutes: number): number {
    return (
      (Math.floor(hours) || 0) * MINUTES_PER_HOUR + (Math.floor(minutes) || 0)
    )
  }

  public getInductionInitialValues(): CompetencyInductionFormValues {
    const answers: Record<string, ITemplateQuestion> = {}
    this.getFlatQuestions().forEach((q) => {
      answers[q.id] = TemplateQuestion.withAllAnswersDeselected(q)
    })
    return { answers }
  }

  public buildSubmissionAnswersWrapper(payload: {
    answeredQuestions: Array<ITemplateQuestion>
    startTime: string
    endTime: string
  }): IAnswersWrapper {
    const normalised = payload.answeredQuestions.map((q) => ({
      ...q,
      requiresProof: q.requiresProof ?? 'NOT_REQUIRED',
      requiresSignature: q.requiresSignature ?? 'NOT_REQUIRED',
      requiresAdditionalInformation:
        q.requiresAdditionalInformation ?? 'NOT_REQUIRED',
    }))
    return {
      trackingId: uuidv4(),
      answers: CompetencyTemplateVersion.wrapQuestionsAsGroups(normalised),
      wrapperSessionTracking: {
        startTime: payload.startTime,
        endTime: payload.endTime,
      },
    } as IAnswersWrapper
  }

  public static wrapQuestionsAsGroups(
    questions: Array<ITemplateQuestion>,
  ): Array<ITemplateQuestionGroup> {
    return questions.map((q) => ({
      id: uuidv4(),
      singular: true,
      data: [q],
    }))
  }

  public static statusString(status: CompetencyTemplateVersionStatus): string {
    switch (status) {
      case 'DRAFT':
        return statuses.draft.label
      case 'ACTIVE':
        return statuses.active.label
      case 'ARCHIVED':
        return statuses.archived.label
      case 'DISCARDED':
        return statuses.discarded.label
      default:
        return status
    }
  }

  public getStatusString(): string {
    return CompetencyTemplateVersion.statusString(this.status)
  }

  public static statusChipColor(
    status: CompetencyTemplateVersionStatus,
  ): StatusColor | 'default' {
    switch (status) {
      case 'ACTIVE':
        return statuses.active.color
      case 'DISCARDED':
        return statuses.discarded.color
      case 'DRAFT':
        return statuses.draft.color
      case 'ARCHIVED':
        return statuses.archived.color
      default:
        return 'default'
    }
  }

  public getStatusChipColor() {
    return CompetencyTemplateVersion.statusChipColor(this.status)
  }
}
